import { useState, useCallback, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  intakeFormSchema,
  step1Schema,
  step2Schema,
  step3Schema,
  step4Schema,
  step5Schema,
  step6Schema,
  type IntakeFormData,
} from '@orientamos/shared'
import { createIntake, updateIntake } from '@lib/api'

const sectionIds = ['contract-type', 'property', 'tenant', 'guarantors', 'owners', 'terms']

// Default values for the form
const getDefaultValues = (): IntakeFormData => ({
  contractType: 'residential',
  guaranteedRent: false,
  property: {
    address: '',
    buildingName: '',
    propertyType: 'apartamento',
    stratum: 3,
    area: 0,
    city: 'Medellin',
    parking: 0,
    storage: 0,
  },
  tenant: {
    personType: 'natural',
    firstName: '',
    lastName: '',
    idNumber: '',
    idIssuedAt: '',
    profession: '',
    phone: '',
    address: '',
    city: 'Medellin',
    email: '',
    invoiceEmail: '',
    role: 'tenant',
    sortOrder: 0,
    banking: null,
    documents: null,
    dataAuthorizationAccepted: false,
    referenceCheckAuthorized: false,
  },
  guarantors: [],
  owners: [
    {
      personType: 'natural',
      firstName: '',
      lastName: '',
      idNumber: '',
      idIssuedAt: '',
      profession: '',
      phone: '',
      address: '',
      city: 'Medellin',
      email: '',
      invoiceEmail: '',
      role: 'owner',
      sortOrder: 0,
      banking: {
        accountNumber: '',
        bankName: '',
        accountType: 'ahorros',
        accountHolderName: '',
        accountHolderId: '',
      },
      documents: null,
      dataAuthorizationAccepted: false,
      referenceCheckAuthorized: false,
    },
  ],
  terms: {
    monthlyRent: 0,
    administrationFee: 0,
    adminFeePaidBy: 'tenant',
    startDate: '',
    endDate: '',
    durationMonths: 12,
    rentIncreaseFormula: 'IPC',
    gracePeriodDays: 5,
  },
  insurance: null,
  commission: {
    percentage: 8,
    comprehensiveCoverage: false,
    utilitiesCoverage: false,
    damagesCoverage: false,
  },
  additionalClauses: null,
  capturedBy: null,
  rentedBy: null,
})

interface UseIntakeFormOptions {
  initialData?: Partial<IntakeFormData>
  intakeId?: string
  autoSave?: boolean
}

export function useIntakeForm(options: UseIntakeFormOptions = {}) {
  const { initialData, intakeId, autoSave = true } = options

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [draftId, setDraftId] = useState<string | null>(intakeId || null)
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const form = useForm({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(intakeFormSchema) as any,
    defaultValues: {
      ...getDefaultValues(),
      ...initialData,
    },
    mode: 'onChange',
  })

  // Auto-save functionality with debounce
  useEffect(() => {
    if (!autoSave) return

    const subscription = form.watch(() => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }

      autoSaveTimeoutRef.current = setTimeout(() => {
        saveDraft()
      }, 3000)
    })

    return () => {
      subscription.unsubscribe()
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [autoSave])

  const validateAllSections = useCallback(async (): Promise<string | null> => {
    const values = form.getValues()
    const sectionSchemas = [
      {
        id: 'contract-type',
        schema: step1Schema,
        data: { contractType: values.contractType, guaranteedRent: values.guaranteedRent },
      },
      { id: 'property', schema: step2Schema, data: { property: values.property } },
      { id: 'tenant', schema: step3Schema, data: { tenant: values.tenant } },
      { id: 'guarantors', schema: step4Schema, data: { guarantors: values.guarantors } },
      { id: 'owners', schema: step5Schema, data: { owners: values.owners } },
      { id: 'terms', schema: step6Schema, data: { terms: values.terms } },
    ]

    for (const { id, schema, data } of sectionSchemas) {
      try {
        schema.parse(data)
      } catch (e) {
        if (e instanceof z.ZodError) {
          e.errors.forEach((zodErr: z.ZodIssue) => {
            const path = zodErr.path.join('.')
            form.setError(path as keyof IntakeFormData, { message: zodErr.message })
          })
        }
        return id
      }
    }
    return null
  }, [form])

  const saveDraft = useCallback(async () => {
    if (isSaving) return

    setIsSaving(true)
    try {
      const data = form.getValues()

      if (draftId) {
        await updateIntake(draftId, data)
      } else {
        const result = await createIntake(data)
        if (result.success && result.data) {
          setDraftId(result.data.id)
        }
      }
    } catch (error) {
      console.error('Error saving draft:', error)
    } finally {
      setIsSaving(false)
    }
  }, [draftId, form, isSaving])

  const submitForm = useCallback(async () => {
    setIsSubmitting(true)
    try {
      const invalidSection = await validateAllSections()
      if (invalidSection) {
        return {
          success: false as const,
          error: 'Por favor complete todos los campos requeridos',
          invalidSection,
        }
      }

      const data = form.getValues()

      if (draftId) {
        const result = await updateIntake(draftId, data)
        if (result.success) {
          return { success: true as const, id: draftId }
        }
        return { success: false as const, error: result.error }
      } else {
        const result = await createIntake(data)
        if (result.success && result.data) {
          return { success: true as const, id: result.data.id }
        }
        return { success: false as const, error: result.error }
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      return { success: false as const, error: 'Error al guardar la orden' }
    } finally {
      setIsSubmitting(false)
    }
  }, [draftId, form, validateAllSections])

  return {
    form,
    isSubmitting,
    isSaving,
    draftId,
    sectionIds,
    submitForm,
    saveDraft,
    validateAllSections,
  }
}
